import { Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TabsModule } from "ngx-bootstrap/tabs";
import { CommonModule } from '@angular/common';
import { BaseComponent } from '@wms/components';
import { VariableCrudManagerService, BaseVariableService, VariablesTemplateComponent } from '@wms/variables';
import { FilterPipe } from '@wm/components-base';
import { FormsModule } from '@angular/forms';
import { forEach} from 'lodash-es';
import { WmsCriteriaBuilderComponent } from '@wms/app/filter-criteria/components/wms-criteria-builder/wms-criteria-builder.component';
import { WmsExpressionErrorDirective } from '@wms/app/directives/wms-expression-error/wms-expression-error.directive';
import {debounceTime, Subscription} from "rxjs";

@Component({
  standalone: true,
  selector: 'wms-variable-tab-view',
  templateUrl: './variable-tab-view.component.html',
  styleUrls: ['./variable-tab-view.component.less'],
  imports: [TooltipModule, TabsModule, CommonModule, FormsModule, WmsCriteriaBuilderComponent, VariablesTemplateComponent, FilterPipe, WmsExpressionErrorDirective]
})
export class VariableTabViewComponent extends BaseComponent implements OnDestroy {
  @Input() disableBinding: boolean;
  propertyGroups: any;
  private subscriptions: Subscription = new Subscription();

  constructor(public VariableCrudManagerService: VariableCrudManagerService, public variableService: BaseVariableService) {
    super();
    this.registerDestroyListener.add(this.eventManager.updateVariableTabViewAsObservable().subscribe(() => {
      this.setObserverForPropertyGroup();
    }));
    this.setObserverForPropertyGroup();
  }

  setObserverForPropertyGroup() {
    if (this.variableService.selectedVariable) {
      let selectedVariableInstance = this.variableService.getSelectedVariableInstance();
      if (selectedVariableInstance) {
        this.setPropertyGroups();
        const propSubject: any = selectedVariableInstance.propertyGroupChangeSubject ? selectedVariableInstance.propertyGroupChangeSubject : selectedVariableInstance[this.variableService.variableServiceMapper[selectedVariableInstance.category]]?.propertyGroupChangeSubject;
        this.subscriptions.add(propSubject?.pipe(debounceTime(100)).subscribe(() => {
          this.setPropertyGroups();
        }));
      }
    }
  }

  setPropertyGroups() {
    this.propertyGroups = this.VariableCrudManagerService.propertyGroups.filter((group: any) => {
      return this.shouldRenderTab(group);
    });
  }

  resetOrder = (a: any, b: any): number => {
    const indexa = this.variableService.tabOrder.indexOf(a.key);
    const indexb = this.variableService.tabOrder.indexOf(b.key);
    return indexa > indexb ? 1 : (indexb > indexa ? -1 : 0);
  }

  handleModelChange(changeObj: any) {
    if (changeObj.propertyName === 'orderBy') {
      this.variableService.selectedVariable.propertyObj['orderBy'].typeaheadvalue = changeObj.$event;
    } else {
      this.variableService.selectedVariable.propertyObj[changeObj.propertyName].modelObj[this.variableService.selectedVariable.propertyObj[changeObj.propertyName].modelKey] = changeObj.$event;
    }
    if (this.variableService.selectedVariable.category === 'wm.Variable') {
      let filteredOptions = this.variableService.selectedVariable.propertyObj[changeObj.propertyName].options;
      forEach(filteredOptions, (options: any) => {
        if (options.name === changeObj.$event) {
          changeObj.$event = options;
        }
      });
      this.variableService.jsonTextEditor.value = JSON.stringify(this.variableService.selectedVariable.variableObject.dataSet,undefined,2);
    }
    changeObj.selectedService?.__onPropertyChange(changeObj.propertyName, changeObj.$event);
    this.setPropertyGroups();
  }

  showFilterCriteria(group: any, propertyGroups?: any) {
    return group.name === 'data' && this.variableService.selectedVariable.variableObject.operation === 'read';
  }

  getTooltipText(subGroup: any) {
    return this.locale['MESSAGE_HELP_VARIABLE']['HEADER_HELP_TEXT'][subGroup.name.toUpperCase()] || (this.locale['MESSAGE_HELP_VARIABLE'][this.variableService.selectedVariable.category] &&
      this.locale['MESSAGE_HELP_VARIABLE'][this.variableService.selectedVariable.category]['HEADER_HELP_TEXT'] && this.locale['MESSAGE_HELP_VARIABLE'][this.variableService.selectedVariable.category]['HEADER_HELP_TEXT'][subGroup.name.toUpperCase()]);
  }

  showModelKey(propertyName: string) {
    const modelKey: any = this.variableService.selectedVariable.propertyObj[propertyName].modelObj[this.variableService.selectedVariable.propertyObj[propertyName].modelKey];
    return modelKey && modelKey.toString().indexOf('bind:') === 0;
  }

  /*
  * clears the current bound value against the provided property of selected variable
  * @param {property} string name of the property against which binding is to be cleared
  */
  clearBinding(propertyDetails: any) {
    propertyDetails.modelObj[propertyDetails.modelKey] = '';
    this.variableService.markUpdated();
  };

  shouldRenderTab = (group: any) => {
    return !(this.variableService.createOnly && group.name === 'events') && this.showTab(group);
  }

  /* returns 'true' if at least one property of at least one subgroup of the the given group is to be shown, else false. */
  showTab(group: any) {
    let showTabFlag = false;
    forEach(group.subGroups,(subGroup) => {
      if (this.showSubGroup(group, subGroup)) {
        showTabFlag = true;
      }
    });
    return showTabFlag;
  };

  /* returns 'true' if at least one property of the given subgroup is to be shown, else false. */
  showSubGroup(group: any, subGroup: any) {
    /* Do not show the subgroup if a widget is not selected */
    if (!this.variableService.selectedVariable) {
      return false;
    }
    let i = 0,
      showSubGroup = false,
      variableProps,
      propName;

    variableProps = this.variableService.selectedVariable.propertyObj;
    propName = null;

    /* Do not show the subgroup if it does not have any properties to be displayed */
    if (!variableProps || !subGroup?.properties) {
        return false;
    }

    /* Check for the properties inside the subgroup that have show property as true. */
    for (i = 0; i < subGroup.properties.length; i += 1) {
      propName = subGroup.properties[i];

      if (subGroup.name === "bindings") {
        if (variableProps[propName] && variableProps[propName].modelObj[variableProps[propName].modelKey] && variableProps[propName].modelObj[variableProps[propName].modelKey].indexOf('bind:') === 0) {
          showSubGroup = true;
          break;
        }
      } else if (variableProps[propName] && !variableProps[propName].hide && !variableProps[propName].isprimary) {
        showSubGroup = true;
        break;
      }
    }
    return showSubGroup;
  };

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.unsubscribe();
  }

}
